import { useLayoutEffect } from "react";

/**
 * Fade-and-rise reveal for every [data-reveal] element on the page.
 *
 * The CSS lives in styles/premium.css. Each element goes wait -> in -> done:
 * hidden before the first paint, animated in when it scrolls into view, and
 * finally released so its own hover transitions work again.
 *
 * It is built so content can never get stuck hidden:
 *   - whatever is already on screen at mount is revealed on a short timer, not
 *     by the observer, so the hero never depends on it;
 *   - if the observer has not reported at all within FAILSAFE_MS (a hidden or
 *     prerendered tab, a screenshot tool, an odd crawler), everything is shown;
 *   - reduced-motion visitors and browsers without IntersectionObserver get the
 *     content straight away.
 *
 * @param {Array} deps re-scan when these change (e.g. a list that loads later).
 */
const FAILSAFE_MS = 1500;
const SETTLE_MS = 1100;

export default function useReveal(deps = []) {
  useLayoutEffect(() => {
    const els = Array.from(document.querySelectorAll("[data-reveal]")).filter(
      (el) => {
        const state = el.getAttribute("data-reveal");
        return state !== "in" && state !== "done" && state !== "wait";
      }
    );
    if (!els.length) return undefined;

    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce || typeof window.IntersectionObserver === "undefined") {
      els.forEach((el) => el.setAttribute("data-reveal", "done"));
      return undefined;
    }

    const timers = [];
    const later = (fn, ms) => timers.push(setTimeout(fn, ms));

    const reveal = (el) => {
      if (el.getAttribute("data-reveal") !== "wait") return;
      el.setAttribute("data-reveal", "in");

      // transitionend bubbles from children, so only trust our own; the timer
      // covers the case where it never fires at all (display:none, a hidden
      // tab). That timer is deliberately not in `timers`: cleanup must not
      // cancel it, or an element caught mid-fade by a re-scan stays "in".
      const delay =
        parseFloat(getComputedStyle(el).getPropertyValue("--reveal-delay")) || 0;
      function finish() {
        el.removeEventListener("transitionend", onEnd);
        if (el.getAttribute("data-reveal") === "in") {
          el.setAttribute("data-reveal", "done");
        }
      }
      function onEnd(e) {
        if (e.target === el) finish();
      }
      el.addEventListener("transitionend", onEnd);
      setTimeout(finish, SETTLE_MS + delay);
    };

    let observerAlive = false;
    const io = new IntersectionObserver(
      (entries) => {
        observerAlive = true;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          reveal(entry.target);
        });
      },
      // threshold 0, not a fraction: any overlap counts. With 0.1 an element
      // taller than ~10 viewports, or one near the end of the page, may never
      // get 10% of itself above the root's -8% edge and stays hidden for good.
      { rootMargin: "0px 0px -8% 0px", threshold: 0 }
    );

    // Layout is ready inside useLayoutEffect, so we can tell what is on screen
    // right now. Those play as an entrance; the rest wait for the observer.
    const viewH = window.innerHeight || document.documentElement.clientHeight;
    const onScreen = [];
    els.forEach((el) => {
      el.setAttribute("data-reveal", "wait");
      const r = el.getBoundingClientRect();
      if (r.top < viewH && r.bottom > 0) onScreen.push(el);
      else io.observe(el);
    });

    // One tick so the hidden state is painted first and the fade actually runs.
    later(() => onScreen.forEach(reveal), 40);

    later(() => {
      if (observerAlive) return;
      io.disconnect();
      els.forEach(reveal);
    }, FAILSAFE_MS);

    return () => {
      io.disconnect();
      timers.forEach(clearTimeout);
      // Deps changed or the page unmounted before an element was seen: never
      // leave it stuck hidden.
      els.forEach((el) => {
        if (el.getAttribute("data-reveal") === "wait") {
          el.setAttribute("data-reveal", "done");
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
